/**
 * M3 FLIGHT-FEEL A6: speed-line streaks — a client-only LineSegments field that
 * follows the camera (floating-origin, like starfield.followCamera) and STREAMS
 * each streak BACKWARD along the ship's velocity so motion reads as SPEED.
 *
 * FIX: the streaks now SCROLL (each has a flow position along velocity that advances
 * every frame and wraps), instead of sitting on fixed points and merely stretching
 * (which looked static). Pure presentation: deterministic seeded layout, no runtime
 * Math.random, no sim-core involvement. Visible only above ~30% max speed.
 */
import * as THREE from 'three';

const COUNT = 220; // streak segments
const FIELD_KM = 400; // half-extent of the streak cloud (lateral spread + flow range)
const MIN_SPEED01 = 0.3; // below this, lines fade out entirely
const BASE_LEN_KM = 5; // streak length at the low end
const SPEED_LEN_KM = 40; // added length at full speed01
const BOOST_LEN_KM = 70; // extra length during boost
// Perceptual flow speeds (km/s) — decoupled from true km/s so streaks visibly
// stream at sane in-system speeds. Streaks traverse the field in well under a second.
const BASE_FLOW = 220;
const SPEED_FLOW = 1500;
const BOOST_FLOW = 1200;

/** Deterministic hash in [0,1) from an integer seed (no Math.random at runtime). */
const hash = (n: number): number => {
  const s = Math.sin(n * 12.9898) * 43758.5453;
  return s - Math.floor(s);
};

export class SpeedLines {
  readonly object: THREE.LineSegments;
  private readonly positions: Float32Array;
  private readonly geo: THREE.BufferGeometry;
  private readonly mat: THREE.LineBasicMaterial;
  /** Stable lateral spread points (camera-relative), one per streak. */
  private readonly base: Float32Array;
  /** Per-streak scroll position along velocity (wraps in [-FIELD, FIELD]). */
  private readonly flow: Float32Array;

  constructor() {
    this.base = new Float32Array(COUNT * 3);
    this.flow = new Float32Array(COUNT);
    for (let i = 0; i < COUNT; i++) {
      this.base[i * 3] = (hash(i + 1) - 0.5) * 2 * FIELD_KM;
      this.base[i * 3 + 1] = (hash(i + 101) - 0.5) * 2 * FIELD_KM;
      this.base[i * 3 + 2] = (hash(i + 201) - 0.5) * 2 * FIELD_KM;
      this.flow[i] = (hash(i + 301) - 0.5) * 2 * FIELD_KM; // staggered so they don't wrap in unison
    }
    this.positions = new Float32Array(COUNT * 6);
    this.geo = new THREE.BufferGeometry();
    this.geo.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.mat = new THREE.LineBasicMaterial({
      color: 0x9fe6f0,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.object = new THREE.LineSegments(this.geo, this.mat);
    this.object.frustumCulled = false;
    this.object.renderOrder = 2;
  }

  /**
   * Update + scroll streaks for this frame.
   * @param dt          frame seconds (drives the scroll)
   * @param camWorldPos camera world position (floating-origin follow)
   * @param velocity    ship world velocity (km/s) — sets the streak direction
   * @param speed01     0..1 speed fraction
   * @param boost01     0..1 boost remaining (ramps length + brightness)
   * @param suppress    hide entirely (e.g. supercruise)
   */
  update(
    dt: number,
    camWorldPos: THREE.Vector3,
    velocity: { x: number; y: number; z: number },
    speed01: number,
    boost01: number,
    suppress = false,
  ): void {
    this.object.position.copy(camWorldPos);
    if (suppress || speed01 < MIN_SPEED01) {
      this.mat.opacity = 0;
      this.object.visible = false;
      return;
    }
    this.object.visible = true;
    const ramp = (speed01 - MIN_SPEED01) / (1 - MIN_SPEED01);
    this.mat.opacity = Math.min(0.7, 0.25 + 0.45 * ramp + 0.3 * boost01);
    const len = BASE_LEN_KM + SPEED_LEN_KM * ramp + BOOST_LEN_KM * boost01;
    const flowStep = (BASE_FLOW + SPEED_FLOW * ramp + BOOST_FLOW * boost01) * dt;
    this.writeSegments(velocity, len, flowStep);
  }

  /** Scroll each streak backward along velocity (wrapping), then lay its segment. */
  private writeSegments(velocity: { x: number; y: number; z: number }, len: number, flowStep: number): void {
    const vlen = Math.hypot(velocity.x, velocity.y, velocity.z) || 1;
    const ux = velocity.x / vlen;
    const uy = velocity.y / vlen;
    const uz = velocity.z / vlen;
    const half = len / 2;
    for (let i = 0; i < COUNT; i++) {
      let f = this.flow[i]! - flowStep; // stream backward (toward/behind the camera)
      if (f < -FIELD_KM) f += 2 * FIELD_KM; // wrap to the front
      this.flow[i] = f;
      const cx = this.base[i * 3]! + ux * f;
      const cy = this.base[i * 3 + 1]! + uy * f;
      const cz = this.base[i * 3 + 2]! + uz * f;
      const o = i * 6;
      this.positions[o] = cx + ux * half;
      this.positions[o + 1] = cy + uy * half;
      this.positions[o + 2] = cz + uz * half;
      this.positions[o + 3] = cx - ux * half;
      this.positions[o + 4] = cy - uy * half;
      this.positions[o + 5] = cz - uz * half;
    }
    (this.geo.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
  }
}
